import React, { useState } from 'react';
import { FormikProvider, useFormik } from 'formik';
import { Modal } from '@round/whitelabel-ui';
import { Button } from '@round/whitelabel-ui';
import { FormField } from '@round/whitelabel-ui';
import { Toggle } from '@round/whitelabel-ui';
import { creatorbase } from '@round/api';
import { showNotification } from 'helpers';
import styles from './CreateLinkModal.module.css';

interface CreateLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
    reportId: string;
    onCreateSuccess: () => void;
    onPublishReport: (payload: creatorbase.PostPublicReportData) => Promise<void>;
}

interface CreateLinkFormData {
    publicReportName: string;
    includeCosts: boolean;
    showBenchmark: boolean;
    includeAdCodes: boolean;
}

const CreateLinkModal: React.FC<CreateLinkModalProps> = ({
    isOpen,
    onClose,
    reportId,
    onCreateSuccess,
    onPublishReport,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const form = useFormik<CreateLinkFormData>({
        initialValues: {
            publicReportName: '',
            includeCosts: false,
            showBenchmark: true,
            includeAdCodes: false,
        },
        onSubmit: async (values) => {
            await handleCreateLink(values);
        },
    });

    const handleCreateLink = async (values: CreateLinkFormData) => {
        if (!values.publicReportName.trim()) {
            showNotification('Please enter a link name', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const payload = {
                report_id: reportId,
                name: values.publicReportName.trim(),
                include_cost: values.includeCosts,
                include_benchmark: values.showBenchmark,
                include_ad_codes: values.includeAdCodes,
            };

            await onPublishReport(payload);

            form.resetForm();
            onClose();
            onCreateSuccess();
        } catch (error) {
            showNotification('Could not create link', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        form.resetForm();
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} closeOnEscape={true} closeOnOverlayClick={true}>
            <Modal.Header>
                <Modal.Title className={styles.modalTitle}>Create Shareable Link</Modal.Title>
            </Modal.Header>
            <Modal.Body className={styles.modalBody}>
                <FormikProvider value={form}>
                    <div className={styles.form}>
                        <FormField
                            name="publicReportName"
                            label="Link Name"
                            placeholder="e.g. Report for artist"
                            className={styles.field}
                        />
                        <div className={styles.costField}>
                            <div className={styles.costRow}>
                                <div className={styles.costLabel}>
                                    <div
                                        className={form.values.includeCosts ? styles.greenCircle : styles.greyCircle}
                                    ></div>
                                    <label htmlFor="include-costs" className={styles.costLabelText}>
                                        Show Costs
                                    </label>
                                </div>
                                <Toggle
                                    value={form.values.includeCosts}
                                    onChange={(value) => form.setFieldValue('includeCosts', value)}
                                    ariaLabel="Show Costs"
                                />
                            </div>
                        </div>
                        <div className={styles.benchmarkField}>
                            <div className={styles.benchmarkRow}>
                                <div className={styles.benchmarkLabel}>
                                    <div
                                        className={form.values.showBenchmark ? styles.greenCircle : styles.greyCircle}
                                    ></div>
                                    <label htmlFor="show-benchmark" className={styles.benchmarkLabelText}>
                                        Show benchmarks
                                    </label>
                                </div>
                                <Toggle
                                    value={form.values.showBenchmark}
                                    onChange={(value) => form.setFieldValue('showBenchmark', value)}
                                    ariaLabel="Show benchmarks"
                                />
                            </div>
                        </div>
                        <div className={styles.adCodeField}>
                            <div className={styles.adCodeRow}>
                                <div className={styles.adCodeLabel}>
                                    <div
                                        className={form.values.includeAdCodes ? styles.greenCircle : styles.greyCircle}
                                    ></div>
                                    <label htmlFor="include-ad-codes" className={styles.adCodeLabelText}>
                                        Show ad codes
                                    </label>
                                </div>
                                <Toggle
                                    value={form.values.includeAdCodes}
                                    onChange={(value) => form.setFieldValue('includeAdCodes', value)}
                                    ariaLabel="Show ad codes"
                                />
                            </div>
                        </div>
                    </div>
                </FormikProvider>
            </Modal.Body>
            <Modal.Actions>
                <Button
                    appearance="primary"
                    className={styles.fullWidthButton}
                    onClick={() => form.submitForm()}
                    isLoading={isLoading}
                    disabled={!form.values.publicReportName.trim()}
                >
                    Create Link
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreateLinkModal;
